/**
 * face-api.js inference engine (the incumbent).
 *
 * This file owns everything face-api specific: weight download, shader
 * warm-up, the per-frame inference call, and pose estimation from its
 * 68-point landmark set. Everything downstream of `detect()` — the
 * gates, their thresholds, the stall watchdog, the auto-capture hold
 * and the funnel telemetry — lives in useFaceDetection.js and is shared
 * with the MediaPipe engine, so an A/B between the two compares
 * inference only.
 *
 * Known constraint, and the reason the other engine exists:
 * face-api.js 0.22.2 has been unmaintained since 2020 and pins
 * @tensorflow/tfjs-core 1.7.0. Production telemetry puts median warm-up
 * at 12.8s on iOS and 5.3s on Android for 556KB of weights, almost all
 * of it WebGL shader compilation that a modern tfjs would compile in
 * parallel.
 */
import * as faceapi from "face-api.js";

const MODEL_URL = "/models/face-api";

let _downloadPromise = null;
let _downloadMs = null;
let _warmupMs = null;
let _backendMs = null;
let _detectorMs = null;
let _landmarksMs = null;
let _diagnostics = null;
let _warmedUp = false;

const probe = (fn) => {
  try {
    const v = fn();
    return v === undefined ? null : v;
  } catch {
    return null;
  }
};

// Read the live WebGL context off the tfjs backend rather than creating a
// throwaway one — a second context can resolve different capabilities (and
// on iOS can evict the first), so only the backend's own context is
// meaningful here.
const collectBackendDiagnostics = () => {
  const tf = probe(() => faceapi.tf);
  if (!tf) return { backend: null };
  const gl = probe(() => tf.backend().gpgpu.gl);
  const flag = (name) => probe(() => tf.env().get(name));
  return {
    tf_version: probe(() => tf.version_core),
    backend: probe(() => tf.getBackend()),
    webgl_version: flag("WEBGL_VERSION"),
    parallel_compile_supported: gl
      ? !!probe(() => gl.getExtension("KHR_parallel_shader_compile"))
      : null,
    float32_enabled: flag("WEBGL_RENDER_FLOAT32_ENABLED"),
    force_f16: flag("WEBGL_FORCE_F16_TEXTURES"),
    pack: flag("WEBGL_PACK"),
    max_texture_size: gl ? probe(() => gl.getParameter(gl.MAX_TEXTURE_SIZE)) : null,
    renderer: gl
      ? probe(() => {
          const ext = gl.getExtension("WEBGL_debug_renderer_info");
          const raw = ext
            ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)
            : gl.getParameter(gl.RENDERER);
          return raw ? String(raw).slice(0, 80) : null;
        })
      : null,
  };
};

// Per-engine capture floors (see mediapipeEngine.limits). face-api
// keeps the global gates; empty means "no override".
export const limits = {};

export function load() {
  if (_downloadPromise) return _downloadPromise;
  _downloadPromise = (async () => {
    // Load both nets in parallel — each loadFromUri is two serial
    // round-trips (manifest then shard), so loading the nets one
    // after the other doubled the serial round-trip count on slow
    // network paths for no reason.
    const loads = [];
    if (!faceapi.nets.tinyFaceDetector.isLoaded) {
      loads.push(faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL));
    }
    if (!faceapi.nets.faceLandmark68Net.isLoaded) {
      loads.push(faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL));
    }
    const tLoad = performance.now();
    await Promise.all(loads);
    _downloadMs = Math.round(performance.now() - tLoad);
  })().catch((err) => {
    _downloadPromise = null;
    throw err;
  });
  return _downloadPromise;
}

/**
 * Run one throwaway detection on a blank canvas after the weights
 * arrive. Fetching weights alone doesn't touch the TF.js backend — the
 * first real detectAllFaces call otherwise pays WebGL context init plus
 * shader compilation on the first live camera frame. Uses the same
 * inputSize as the live loop so the compiled programs are the ones the
 * loop reuses. Non-fatal: a warm-up failure never blocks the flow.
 */
export async function warmUp() {
  if (_warmedUp) return;
  _warmedUp = true;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 224;
    canvas.height = 224;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#808080";
    ctx.fillRect(0, 0, 224, 224);

    // Backend init (WebGL context creation + program cache setup) is
    // separate from the per-net compiles below. Timing it apart tells us
    // whether the cost is context setup or the nets themselves.
    const tBackend0 = performance.now();
    try {
      if (faceapi.tf && faceapi.tf.ready) await faceapi.tf.ready();
    } catch (e) {
      // Non-fatal: the detect calls below initialise the backend anyway.
    }
    _backendMs = Math.round(performance.now() - tBackend0);
    _diagnostics = collectBackendDiagnostics();

    const t0 = performance.now();
    await faceapi.detectAllFaces(
      canvas,
      new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 })
    );
    const tDetector = performance.now();
    // Yield a macrotask between the two compiles — shader compilation
    // blocks the main thread for the whole net, so this gap is the one
    // window where queued clicks and paints can get through mid-warm-up.
    await new Promise((resolve) => setTimeout(resolve, 150));
    // A blank canvas yields 0 detections, so .withFaceLandmarks() would
    // never execute the landmark net here — its shaders would still
    // compile on the first real frame with a face. detectFaceLandmarks
    // runs the net with no detector gate, forcing that compile now.
    await faceapi.detectFaceLandmarks(canvas);
    const t1 = performance.now();
    _warmupMs = Math.round(t1 - t0);
    _detectorMs = Math.round(tDetector - t0);
    // Subtract the deliberate 150ms yield so landmarks_ms is compile
    // time, not compile time plus our own pause.
    _landmarksMs = Math.round(t1 - tDetector - 150);
  } catch (err) {
    _warmedUp = false;
    _diagnostics = { ..._diagnostics, warmup_error: String(err).slice(0, 120) };
    console.warn("[FaceDetect] warm-up inference failed (non-fatal):", err);
  }
}

let _detectOptions = null;

/**
 * Pose from the 68-point landmark set. The MediaPipe engine computes the
 * same three angles from its own equivalent points, so the pose gate
 * means the same thing in both arms.
 */
function estimatePose(landmarks) {
  const pts = landmarks.positions;
  // Roll: angle between outer eye corners (36, 45)
  const leftEye = pts[36];
  const rightEye = pts[45];
  const roll = Math.abs(
    Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x) * (180 / Math.PI)
  );

  // Yaw: nose tip (30) deviation from face midline (0=jaw left, 16=jaw right)
  const noseTip = pts[30];
  const jawLeft = pts[0];
  const jawRight = pts[16];
  const faceMidX = (jawLeft.x + jawRight.x) / 2;
  const faceWidth = Math.abs(jawRight.x - jawLeft.x);
  const yaw = faceWidth > 0
    ? Math.abs(((noseTip.x - faceMidX) / faceWidth) * 120)
    : 0;

  // Pitch: nose tip vertical position relative to eye-chin range.
  // The tip sits at ~35% of eye-to-chin distance in a frontal view.
  const eyeMidY = (leftEye.y + rightEye.y) / 2;
  const chin = pts[8];
  const eyeChinDist = chin.y - eyeMidY;
  const noseFromEyes = noseTip.y - eyeMidY;
  const expectedRatio = 0.35;
  const actualRatio = eyeChinDist > 0 ? noseFromEyes / eyeChinDist : 0.5;
  const pitch = Math.abs((actualRatio - expectedRatio) * 120);

  // pitchRatio is the raw input to the pitch constant so the two
  // engines' calibrations can be compared from capture telemetry.
  return { yaw, pitch, roll, pitchRatio: actualRatio };
}

/**
 * One inference. Returns the shared shape both engines produce:
 * `{ count, box, pose }` with box in source-video pixels.
 */
export async function detect(video) {
  if (!_detectOptions) {
    _detectOptions = new faceapi.TinyFaceDetectorOptions({
      inputSize: 224,
      scoreThreshold: 0.5,
    });
  }
  const detections = await faceapi
    .detectAllFaces(video, _detectOptions)
    .withFaceLandmarks();

  if (detections.length !== 1) return { count: detections.length, box: null, pose: null };
  const det = detections[0];
  const b = det.detection.box;
  return {
    count: 1,
    box: { x: b.x, y: b.y, width: b.width, height: b.height },
    pose: estimatePose(det.landmarks),
  };
}

export function getDiagnostics() {
  return {
    download_ms: _downloadMs,
    warmup_ms: _warmupMs,
    backend_ms: _backendMs,
    detector_ms: _detectorMs,
    landmarks_ms: _landmarksMs,
    ..._diagnostics,
  };
}
